import type { ConversationItem } from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { KIND_CLASSES } from '@/components/widget/obligation-kind';
import { useNavigateWithContentFlush } from '@/hooks';
import type { LucideIcon } from 'lucide-react';
import { ExternalLink, Files, ListChecks, Mail } from 'lucide-react';

type ContentReferenceListItem = Extract<ConversationItem, { kind: 'content_reference_list' }>;

export interface ProvenanceContentReferenceListCardProps {
  item: ContentReferenceListItem;
}

const REFERENCE_HEADER: Record<
  ContentReferenceListItem['referenceType'],
  { label: string; Icon: LucideIcon }
> = {
  OBLIGATIONS: { label: 'Obligations', Icon: ListChecks },
  MESSAGES: { label: 'Messages', Icon: Mail },
  CONTENT: { label: 'Content', Icon: Files },
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function dueIndicator(dueAt: string | null | undefined): { label: string; className: string } | null {
  if (!dueAt) return null;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return null;
  const now = Date.now();
  if (due < now) {
    return { label: `Overdue · ${new Date(dueAt).toLocaleDateString()}`, className: 'text-destructive font-medium' };
  }
  if (due - now <= WEEK_MS) {
    return { label: `Due ${new Date(dueAt).toLocaleDateString()}`, className: 'text-warning' };
  }
  return { label: `Due ${new Date(dueAt).toLocaleDateString()}`, className: 'text-muted-foreground' };
}

export function ProvenanceContentReferenceListCard({ item }: ProvenanceContentReferenceListCardProps) {
  const navigate = useNavigateWithContentFlush();
  const header = REFERENCE_HEADER[item.referenceType];
  const HeaderIcon = header.Icon;

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="mb-2 flex items-center gap-2">
        <HeaderIcon className="h-4 w-4 shrink-0" />
        <p className="font-medium">{header.label}</p>
        <Badge variant="outline">{item.items.length}</Badge>
        {item.items.length > 0 ? (
          <button
            type="button"
            onClick={() => navigate(item.items[0].url)}
            className="ml-auto rounded p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
            aria-label="Open source content"
            title="Open source content"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      <ul className="divide-y divide-border/60">
        {item.items.map((ref) => {
          const due = dueIndicator(ref.dueAt);
          return (
            <li key={ref.contentId}>
              <button
                type="button"
                onClick={() => navigate(ref.url)}
                className="block w-full rounded px-1 py-2 text-left transition-colors hover:bg-muted/40"
              >
                <p className="min-w-0 truncate font-medium">{ref.title}</p>
                <div className="mt-1 flex w-full items-center gap-2">
                  {ref.extractKind ? (
                    <span
                      className={`inline-block shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${
                        KIND_CLASSES[ref.extractKind]
                      }`}
                    >
                      {ref.extractKind}
                    </span>
                  ) : null}
                  {due ? <span className={`shrink-0 text-xs ${due.className}`}>{due.label}</span> : null}
                  {ref.subtitle ? (
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{ref.subtitle}</span>
                  ) : null}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
